(() => {
  'use strict';
  const root = () => window.Shopify?.routes?.root || '/';
  const reduce = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const fmt = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });

  // Adds business days, skipping Saturdays and Sundays.
  function addBusinessDays(date, days) {
    const d = new Date(date);
    while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
    for (let left = days; left > 0;) {
      d.setDate(d.getDate() + 1);
      if (d.getDay() !== 0 && d.getDay() !== 6) left--;
    }
    return d;
  }

  function initDrawer(el) {
    const drawer = el.querySelector('[data-drawer]'), openBtn = el.querySelector('[data-drawer-open]');
    if (!drawer || !openBtn) return;
    const set = (open) => {
      drawer.classList.toggle('is-open', open);
      drawer.setAttribute('aria-hidden', String(!open));
      openBtn.setAttribute('aria-expanded', String(open));
      document.documentElement.style.overflow = open ? 'hidden' : '';
      (open ? drawer.querySelector('[data-drawer-close].icon-btn') : openBtn)?.focus();
    };
    openBtn.addEventListener('click', () => set(true));
    drawer.querySelectorAll('[data-drawer-close]').forEach((b) => b.addEventListener('click', () => set(false)));
    drawer.addEventListener('keydown', (e) => { if (e.key === 'Escape') set(false); });
  }

  function initAnnouncement(el) {
    const rot = el.querySelector('[data-ann-rotate]');
    if (rot) {
      const slides = [...rot.children];
      if (slides.length > 1) {
        let i = 0;
        setInterval(() => {
          slides[i].classList.remove('is-on');
          i = (i + 1) % slides.length;
          slides[i].classList.add('is-on');
        }, (Number(rot.dataset.interval) || 4) * 1000);
      }
    }
    const end = Date.parse(el.dataset.end || '');
    const clock = el.querySelector('[data-ann-clock]');
    if (!end || !clock) return;
    const units = { d: 86400, h: 3600, m: 60, s: 1 };
    const tick = () => {
      let left = Math.max(0, Math.floor((end - Date.now()) / 1000));
      if (!left) {
        if (el.dataset.hideEnded === 'true') el.hidden = true;
        clearInterval(timer);
      }
      Object.entries(units).forEach(([u, sec]) => {
        const n = Math.floor(left / sec); left -= n * sec;
        const node = clock.querySelector(`[data-u="${u}"]`);
        if (node) node.textContent = `${String(n).padStart(2, '0')}${u}`;
      });
    };
    const timer = setInterval(tick, 1000); tick();
  }

  function initReveal(scope) {
    const items = scope.querySelectorAll('[data-reveal]');
    if (!items.length) return;
    if (reduce() || !('IntersectionObserver' in window)) { items.forEach((i) => i.classList.add('is-in')); return; }
    const io = new IntersectionObserver((entries) => entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); } }), { rootMargin: '0px 0px -8% 0px' });
    items.forEach((i) => { i.classList.add('reveal'); io.observe(i); });
  }

  function initGallery(el) {
    const track = el.querySelector('[data-pg-track]'), thumbs = [...el.querySelectorAll('[data-pg-thumb]')], dots = [...el.querySelectorAll('[data-pg-dots] span')];
    if (!track) return;
    const count = track.children.length;
    const idx = () => Math.round(track.scrollLeft / track.clientWidth);
    const go = (i) => track.scrollTo({ left: Math.max(0, Math.min(count - 1, i)) * track.clientWidth, behavior: reduce() ? 'auto' : 'smooth' });
    thumbs.forEach((t) => t.addEventListener('click', () => go(Number(t.dataset.pgThumb))));
    el.querySelector('[data-pg-prev]')?.addEventListener('click', () => go(idx() - 1));
    el.querySelector('[data-pg-next]')?.addEventListener('click', () => go(idx() + 1));
    let raf;
    track.addEventListener('scroll', () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const i = idx();
        thumbs.forEach((t, j) => t.setAttribute('aria-current', String(i === j)));
        dots.forEach((d, j) => d.classList.toggle('is-on', i === j));
        thumbs[i]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      });
    }, { passive: true });
  }

  function initDelivery(el) {
    const n = (k) => Math.max(0, Number(el.dataset[k]) || 0);
    const pMin = Math.min(n('procMin'), n('procMax')), pMax = Math.max(n('procMin'), n('procMax'));
    const sMin = Math.min(n('shipMin'), n('shipMax')), sMax = Math.max(n('shipMin'), n('shipMax'));
    const today = new Date();
    const a = addBusinessDays(addBusinessDays(today, pMin), sMin), b = addBusinessDays(addBusinessDays(today, pMax), sMax);
    const daysMin = pMin + sMin, daysMax = pMax + sMax;
    const fill = (tpl) => tpl.replaceAll('[min]', daysMin).replaceAll('[max]', daysMax).replaceAll('[start]', fmt.format(a)).replaceAll('[end]', fmt.format(b));
    el.querySelectorAll('[data-tpl]').forEach((node) => { node.textContent = fill(node.dataset.tpl); });
  }

  // Last order date that still arrives by the "deliver by" day (max processing + shipping days, weekends skipped).
  function initCutoff(el) {
    const box = el.closest('[data-delivery]');
    const pMax = Number(box?.dataset.procMax) || 0, sMax = Number(box?.dataset.shipMax) || 0;
    const [m, d] = (el.dataset.deliverBy || '12-24').split('-').map(Number);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const target = new Date(today.getFullYear(), m - 1, d);
    let cutoff = null;
    for (let c = new Date(target); c >= today; c.setDate(c.getDate() - 1)) {
      if (c.getDay() === 0 || c.getDay() === 6) continue;
      if (addBusinessDays(addBusinessDays(c, pMax), sMax) <= target) { cutoff = new Date(c); break; }
    }
    const text = el.querySelector('[data-cutoff-text]');
    if (cutoff) text.textContent = el.dataset.before.replace('[cutoff]', fmt.format(cutoff));
    else if (el.dataset.after) text.textContent = el.dataset.after;
    else el.hidden = true;
  }

  function initProduct(el) {
    const form = el.querySelector('form[data-product-form]');
    if (!form) return;
    const qty = form.querySelector('[name=quantity]');
    const status = form.querySelector('[data-status]');
    const variants = JSON.parse(el.querySelector('[data-variants]')?.textContent || '[]');
    // form.id would return the hidden input named "id", so read the attribute.
    const fid = form.getAttribute('id');
    const addButtons = () => [...document.querySelectorAll(`[data-add][form="${fid}"], #${fid} [data-add]`)];
    const label = addButtons()[0]?.dataset.label || 'Add to cart';

    el.querySelectorAll('[data-qty]').forEach((b) => b.addEventListener('click', () => {
      const step = Number(qty.step) || 1, min = Number(qty.min) || 1, max = qty.max ? Number(qty.max) : Infinity;
      qty.value = String(Math.max(min, Math.min(max, (Number(qty.value) || min) + Number(b.dataset.qty) * step)));
      qty.dispatchEvent(new Event('change'));
    }));
    const refreshLabels = () => {
      const v = variants.find((x) => String(x.id) === String(form.querySelector('[name=id]').value)) || variants[0];
      const total = v ? v.price_cents * (Number(qty.value) || 1) : 0;
      addButtons().forEach((btn) => {
        btn.disabled = v && !v.available;
        const showTotal = btn.dataset.showTotal === 'true' && v?.available;
        btn.querySelector('[data-add-label]').textContent = v && !v.available ? 'Sold out' : showTotal ? `${label} • ${window.Shopify?.formatMoney ? Shopify.formatMoney(total) : (total / 100).toLocaleString('en-US', { style: 'currency', currency: window.Shopify?.currency?.active || 'USD' })}` : label;
      });
    };
    qty?.addEventListener('change', refreshLabels);
    form.querySelector('select[name=id]')?.addEventListener('change', (e) => {
      const v = variants.find((x) => String(x.id) === e.target.value);
      if (!v) return;
      el.querySelectorAll('[data-price-now]').forEach((n) => (n.textContent = v.price));
      el.querySelectorAll('[data-price-was]').forEach((n) => { n.textContent = v.compare || ''; n.hidden = !v.compare; });
      el.querySelectorAll('[data-price-save]').forEach((n) => { n.hidden = !v.save; if (v.save) n.textContent = n.dataset.tpl.replace('[amount]', v.save).replace('[percent]', v.save_pct); });
      const url = new URL(location.href); url.searchParams.set('variant', v.id); history.replaceState({}, '', url);
      refreshLabels();
    });
    refreshLabels();

    form.addEventListener('submit', async (e) => {
      if (e.submitter && !e.submitter.matches('[data-add]')) return;
      e.preventDefault();
      const btns = addButtons();
      btns.forEach((b) => { b.disabled = true; });
      status.textContent = '';
      try {
        const res = await fetch(`${root()}cart/add.js`, { method: 'POST', body: new FormData(form), headers: { Accept: 'application/json' } });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || 'Could not add this item. Please try again.');
        if (form.dataset.after === 'stay') {
          const cart = await (await fetch(`${root()}cart.js`)).json();
          document.querySelectorAll('[data-cart-count]').forEach((c) => { c.textContent = cart.item_count || ''; c.dataset.count = cart.item_count; });
          btns.forEach((b) => { b.disabled = false; });
          status.textContent = '';
          document.getElementById('shop-status').textContent = 'Added to cart';
        } else {
          location.assign(`${root()}cart`);
        }
      } catch (err) {
        status.textContent = err.message;
        btns.forEach((b) => { b.disabled = false; });
      }
    });

    const sbar = document.querySelector(`[data-sbar="${fid}"]`), main = form.querySelector('[data-add]');
    if (sbar && main && 'IntersectionObserver' in window) {
      new IntersectionObserver(([en]) => {
        const show = !en.isIntersecting && en.boundingClientRect.top < 0;
        sbar.classList.toggle('is-visible', show);
        sbar.setAttribute('aria-hidden', String(!show));
        sbar.querySelectorAll('button,a').forEach((b) => (b.tabIndex = show ? 0 : -1));
      }).observe(main);
    }
  }

  function initVideo(el) {
    const v = el.querySelector('video'), btn = el.querySelector('[data-vid-play]');
    if (!v || !btn) return;
    btn.addEventListener('click', () => { v.muted = false; v.controls = true; v.play(); el.classList.add('is-playing'); });
    v.addEventListener('pause', () => { if (v.ended) el.classList.remove('is-playing'); });
  }

  // Buttons elsewhere on a product page (e.g. "Ways to use it") submit the main product form.
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-add-main]');
    if (!b) return;
    const form = document.querySelector('form[data-product-form]'), add = form?.querySelector('[data-add]');
    if (form && add && !add.disabled) form.requestSubmit(add);
    else document.querySelector('.pi')?.scrollIntoView({ behavior: reduce() ? 'auto' : 'smooth' });
  });

  function init(scope = document) {
    const run = (sel, fn) => scope.querySelectorAll(sel).forEach((el) => { if (el.dataset.ready) return; el.dataset.ready = '1'; fn(el); });
    run('[data-header]', initDrawer);
    run('[data-ann]', initAnnouncement);
    run('[data-pg]', initGallery);
    run('[data-delivery]', initDelivery);
    run('[data-cutoff]', initCutoff);
    run('[data-product]', initProduct);
    run('[data-vid]', initVideo);
    initReveal(scope);
  }
  init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
